import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../../../../../../hooks/useAxios";
import { useEffect, useRef, useState } from "react";
import { useTracking } from "react-tracking";
import {
  CLIENT_CAMPAIGN,
  CLIENT_CAMPAIGNS,
  CLIENT_CATEGORIES,
  CLIENT_CATEGORY,
  CLIENT_MARKET,
  CLIENT_MARKETS,
  CLIENT_PLATFORM_MAPPING,
  CLIENT_PLATFORM_MAPPINGS,
  CLIENT_PRODUCT,
  CLIENT_PRODUCTS,
  CLIENTS_ADMINISTRATION_BY_ID,
} from "../../../../../../shared/lib/apiEndpointsResolver";
import { catchFunction } from "../../../../../../shared/lib/utils";
import BreadcrumbPanel from "../../../../../../shared/components/breadcrumb/BreadcrumbPanel";
import {
  CLIENTS_ADMINISTRATION,
  TOOLS,
} from "../../../../../../shared/lib/routesResolver";
import ClientDetail from "./ClientDetail";
import ClientDetailTabMenu from "./ClientDetailTabMenu";
import SearchInput from "../../../../../../shared/components/input/SearchInput";
import CreateClientMarketDialog from "./market/CreateClientMarketDialog";
import { useTranslation } from "react-i18next";
import useToastWrapper from "../../../../../../hooks/useToastWrapper";
import useConfirmDialog from "../../../../../../shared/components/confirm-dialog/useConfirmDialog";
import CreateClientCampaignDialog from "./campaign/CreateClientCampaignDialog";
import CreateClientCategoryDialog from "./category/CreateClientCategoryDialog";
import CreateClientProductDialog from "./product/CreateClientProductDialog";
import CreatePlatformClientMappingDialog from "./platformMapping/CreatePlatformClientMappingDialog";

const ClientDetailContainer = () => {
  const { id } = useParams();
  const axios = useAxios();
  const [client, setClient] = useState();
  const clientDeleteDialog = useConfirmDialog();
  // Markets
  const [markets, setMarkets] = useState([]);
  const marketsRef = useRef([]);
  const [marketForDeletion, setMarketForDeletion] = useState();
  const marketDeleteDialog = useConfirmDialog();
  const [marketSearch, setMarketSearch] = useState();
  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const campaignsRef = useRef([]);
  const [campaignForDeletion, setCampaignForDeletion] = useState();
  const campaignDeleteDialog = useConfirmDialog();
  const [campaignSearch, setCampaignSearch] = useState();
  // Categories
  const [categories, setCategories] = useState([]);
  const categoriesRef = useRef([]);
  const [categoryForDeletion, setCategoryForDeletion] = useState();
  const categoryDeleteDialog = useConfirmDialog();
  const [categorySearch, setCategorySearch] = useState();
  // Products
  const [products, setProducts] = useState([]);
  const productsRef = useRef([]);
  const [productForDeletion, setProductForDeletion] = useState();
  const productDeleteDialog = useConfirmDialog();
  const [productSearch, setProductSearch] = useState();
  // Platform mappings
  const [platformMappings, setPlatformMappings] = useState([]);
  const platformMappingsRef = useRef([]);
  const [platformMappingForDeletion, setPlatformMappingForDeletion] =
    useState();
  const platformMappingDeleteDialog = useConfirmDialog();
  const [platformMappingSearch, setPlatformMappingSearch] = useState();
  //
  const { trackEvent } = useTracking();
  const { t } = useTranslation();
  const toast = useToastWrapper();
  const navigate = useNavigate();

  const breadcrumbItems = [
    { title: "clients.detail.breadcrumb.first", link: TOOLS },
    { title: "clients.detail.breadcrumb.second", link: CLIENTS_ADMINISTRATION },
    {
      title: "clients.detail.breadcrumb.third",
      titleOptions: { name: client?.name },
    },
  ];

  const findClient = () => {
    axios
      .get(CLIENTS_ADMINISTRATION_BY_ID(id))
      .then((response) => setClient(response.data))
      .catch(catchFunction);
  };

  const onDeleteClientClick = () => {
    clientDeleteDialog.display(
      t("clients.delete.confirmation", { name: client.name }),
    );
  };

  const deleteClient = () => {
    axios
      .delete(CLIENTS_ADMINISTRATION_BY_ID(id))
      .then((_) => {
        toast.success(t("clients.delete.success"));
        navigate(CLIENTS_ADMINISTRATION);
      })
      .catch(catchFunction);
  };

  const editClient = (formValues) => {
    axios
      .put(CLIENTS_ADMINISTRATION_BY_ID(id), formValues)
      .then((response) => {
        toast.success(
          t("clients.updateForm.success", { name: response.data.name }),
        );
        setClient(response.data);
      })
      .catch(catchFunction);
  };

  // *********************************************************************************
  // Markets tab functionalities

  const findClientMarkets = () => {
    axios
      .get(CLIENT_MARKETS(id))
      .then((response) => {
        setMarkets(response.data);
        marketsRef.current = response.data;
      })
      .catch(catchFunction);
  };

  const filterMarketsByName = (changeEvent) => {
    setMarketSearch(changeEvent.target.value);
    setMarkets(
      marketsRef.current.filter((market) =>
        market.name
          .toLowerCase()
          .includes(changeEvent.target.value.toLowerCase()),
      ),
    );
  };

  const onMarketCreation = (formValues) => {
    axios
      .post(CLIENT_MARKETS(id), formValues)
      .then((response) => {
        toast.success(
          t("clients.detail.tabs.markets.createForm.success", {
            name: response.data.name,
          }),
        );
        findClientMarkets();
      })
      .catch(catchFunction);
  };

  const onMarketEdition = (marketId, formValues) => {
    axios
      .put(CLIENT_MARKET(id, marketId), formValues)
      .then((response) => {
        toast.success(
          t("clients.detail.tabs.markets.updateForm.success", {
            name: response.data.name,
          }),
        );
        findClientMarkets();
      })
      .catch(catchFunction);
  };

  const deleteMarket = () => {
    axios
      .delete(CLIENT_MARKET(id, marketForDeletion.id))
      .then((_) => {
        toast.success(
          t("clients.detail.tabs.markets.delete.success", {
            name: marketForDeletion.name,
          }),
        );
        findClientMarkets();
      })
      .catch(catchFunction);
  };

  const onMarketDeletionClick = (id) => {
    const market = marketsRef.current.filter((market) => market.id === id)[0];
    setMarketForDeletion(market);
    marketDeleteDialog.display(
      t("clients.detail.tabs.markets.delete.confirmation", {
        name: market.name,
      }),
    );
  };

  const MarketsOptions = (
    <div className="flex flex-row gap-x-1 items-center">
      <SearchInput
        value={marketSearch}
        onChangeHandler={filterMarketsByName}
        placeholder="clients.detail.tabs.markets.searchInputPlaceholder"
      />
      <CreateClientMarketDialog onSubmitHandler={onMarketCreation} />
    </div>
  );

  // *********************************************************************************
  // Campaigns tab functionalities

  const findClientCampaigns = () => {
    axios
      .get(CLIENT_CAMPAIGNS(id))
      .then((response) => {
        setCampaigns(response.data);
        campaignsRef.current = response.data;
      })
      .catch(catchFunction);
  };

  const filterCampaignsByName = (changeEvent) => {
    setCampaignSearch(changeEvent.target.value);
    setCampaigns(
      campaignsRef.current.filter((campaign) =>
        campaign.name
          .toLowerCase()
          .includes(changeEvent.target.value.toLowerCase()),
      ),
    );
  };

  const onCampaignCreation = (formValues) => {
    axios
      .post(CLIENT_CAMPAIGNS(id), formValues)
      .then((response) => {
        toast.success(
          t("clients.detail.tabs.campaigns.createForm.success", {
            name: response.data.name,
          }),
        );
        findClientCampaigns();
      })
      .catch(catchFunction);
  };

  const onCampaignEdition = (campaignId, formValues) => {
    axios
      .put(CLIENT_CAMPAIGN(id, campaignId), formValues)
      .then((response) => {
        toast.success(
          t("clients.detail.tabs.campaigns.updateForm.success", {
            name: response.data.name,
          }),
        );
        findClientCampaigns();
      })
      .catch(catchFunction);
  };

  const deleteCampaign = () => {
    axios
      .delete(CLIENT_CAMPAIGN(id, campaignForDeletion.id))
      .then((_) => {
        toast.success(
          t("clients.detail.tabs.campaigns.delete.success", {
            name: campaignForDeletion.name,
          }),
        );
        findClientCampaigns();
      })
      .catch(catchFunction);
  };

  const onCampaignDeletionClick = (id) => {
    const campaign = campaignsRef.current.filter(
      (campaign) => campaign.id === id,
    )[0];
    setCampaignForDeletion(campaign);
    campaignDeleteDialog.display(
      t("clients.detail.tabs.campaigns.delete.confirmation", {
        name: campaign.name,
      }),
    );
  };

  const CampaignsOptions = (
    <div className="flex flex-row gap-x-1 items-center">
      <SearchInput
        value={campaignSearch}
        onChangeHandler={filterCampaignsByName}
        placeholder="clients.detail.tabs.campaigns.searchInputPlaceholder"
      />
      <CreateClientCampaignDialog onSubmitHandler={onCampaignCreation} />
    </div>
  );

  // *********************************************************************************
  // Categories tab functionalities

  const findClientCategories = () => {
    axios
      .get(CLIENT_CATEGORIES(id))
      .then((response) => {
        setCategories(response.data);
        categoriesRef.current = response.data;
      })
      .catch(catchFunction);
  };

  const filterCategoriesByName = (changeEvent) => {
    setCategorySearch(changeEvent.target.value);
    setCategories(
      categoriesRef.current.filter((campaign) =>
        campaign.name
          .toLowerCase()
          .includes(changeEvent.target.value.toLowerCase()),
      ),
    );
  };

  const onCategoryCreation = (formValues) => {
    axios
      .post(CLIENT_CATEGORIES(id), formValues)
      .then((response) => {
        toast.success(
          t("clients.detail.tabs.categories.createForm.success", {
            name: response.data.name,
          }),
        );
        findClientCategories();
      })
      .catch(catchFunction);
  };

  const onCategoryEdition = (categoryId, formValues) => {
    axios
      .put(CLIENT_CATEGORY(id, categoryId), formValues)
      .then((response) => {
        toast.success(
          t("clients.detail.tabs.categories.updateForm.success", {
            name: response.data.name,
          }),
        );
        findClientCategories();
      })
      .catch(catchFunction);
  };

  const deleteCategory = () => {
    axios
      .delete(CLIENT_CATEGORY(id, categoryForDeletion.id))
      .then((_) => {
        toast.success(
          t("clients.detail.tabs.categories.delete.success", {
            name: categoryForDeletion.name,
          }),
        );
        findClientCategories();
      })
      .catch(catchFunction);
  };

  const onCategoryDeletionClick = (id) => {
    const category = categoriesRef.current.filter(
      (category) => category.id === id,
    )[0];
    setCategoryForDeletion(category);
    categoryDeleteDialog.display(
      t("clients.detail.tabs.categories.delete.confirmation", {
        name: category.name,
      }),
    );
  };

  const CategoriesOptions = (
    <div className="flex flex-row gap-x-1 items-center">
      <SearchInput
        value={categorySearch}
        onChangeHandler={filterCategoriesByName}
        placeholder="clients.detail.tabs.categories.searchInputPlaceholder"
      />
      <CreateClientCategoryDialog onSubmitHandler={onCategoryCreation} />
    </div>
  );

  // *********************************************************************************
  // Products tab functionalities

  const findClientProducts = () => {
    axios
      .get(CLIENT_PRODUCTS(id))
      .then((response) => {
        setProducts(response.data);
        productsRef.current = response.data;
      })
      .catch(catchFunction);
  };

  const filterProductsByName = (changeEvent) => {
    setProductSearch(changeEvent.target.value);
    setProducts(
      productsRef.current.filter((product) =>
        product.name
          .toLowerCase()
          .includes(changeEvent.target.value.toLowerCase()),
      ),
    );
  };

  const onProductCreation = (formValues) => {
    axios
      .post(CLIENT_PRODUCTS(id), formValues)
      .then((response) => {
        toast.success(
          t("clients.detail.tabs.products.createForm.success", {
            name: response.data.name,
          }),
        );
        findClientProducts();
      })
      .catch(catchFunction);
  };

  const onProductEdition = (productId, formValues) => {
    axios
      .put(CLIENT_PRODUCT(id, productId), formValues)
      .then((response) => {
        toast.success(
          t("clients.detail.tabs.products.updateForm.success", {
            name: response.data.name,
          }),
        );
        findClientProducts();
      })
      .catch(catchFunction);
  };

  const deleteProduct = () => {
    axios
      .delete(CLIENT_PRODUCT(id, productForDeletion.id))
      .then((_) => {
        toast.success(
          t("clients.detail.tabs.products.delete.success", {
            name: productForDeletion.name,
          }),
        );
        findClientProducts();
      })
      .catch(catchFunction);
  };

  const onProductDeletionClick = (id) => {
    const product = productsRef.current.filter(
      (product) => product.id === id,
    )[0];
    setProductForDeletion(product);
    productDeleteDialog.display(
      t("clients.detail.tabs.products.delete.confirmation", {
        name: product.name,
      }),
    );
  };

  const ProductsOptions = (
    <div className="flex flex-row gap-x-1 items-center">
      <SearchInput
        value={productSearch}
        onChangeHandler={filterProductsByName}
        placeholder="clients.detail.tabs.products.searchInputPlaceholder"
      />
      <CreateClientProductDialog id={id} onSubmitHandler={onProductCreation} />
    </div>
  );

  // *********************************************************************************
  // Platform mappings tab functionalities
  const findClientPlatformMappings = () => {
    axios
      .get(CLIENT_PLATFORM_MAPPINGS(id))
      .then((response) => {
        setPlatformMappings(response.data);
        platformMappingsRef.current = response.data;
      })
      .catch(catchFunction);
  };

  const filterPlatformMappingsByCode = (changeEvent) => {
    setPlatformMappingSearch(changeEvent.target.value);
    setPlatformMappings(
      platformMappingsRef.current.filter((platformMapping) =>
        platformMapping.code
          .toLowerCase()
          .includes(changeEvent.target.value.toLowerCase()),
      ),
    );
  };

  const onPlatformMappingCreation = (formValues) => {
    axios
      .post(CLIENT_PLATFORM_MAPPINGS(id), formValues)
      .then((response) => {
        toast.success(
          t("clients.detail.tabs.platformMappings.createForm.success", {
            platform: response.data.platform.name,
            code: response.data.code,
          }),
        );
        findClientPlatformMappings();
      })
      .catch(catchFunction);
  };

  const onPlatformMappingEdition = (platformMappingId, formValues) => {
    axios
      .put(CLIENT_PLATFORM_MAPPING(id, platformMappingId), formValues)
      .then((response) => {
        toast.success(
          t("clients.detail.tabs.platformMappings.updateForm.success", {
            platform: response.data.platform.name,
            code: response.data.code,
          }),
        );
        findClientPlatformMappings();
      })
      .catch(catchFunction);
  };

  const deletePlatformMapping = () => {
    axios
      .delete(CLIENT_PLATFORM_MAPPING(id, platformMappingForDeletion.id))
      .then((_) => {
        toast.success(
          t("clients.detail.tabs.platformMappings.delete.success", {
            platform: platformMappingForDeletion.platform.name,
            code: platformMappingForDeletion.code,
          }),
        );
        findClientPlatformMappings();
      })
      .catch(catchFunction);
  };

  const onPlatformMappingDeletionClick = (id) => {
    const platformMapping = platformMappingsRef.current.filter(
      (platformMapping) => platformMapping.id === id,
    )[0];
    setPlatformMappingForDeletion(platformMapping);
    platformMappingDeleteDialog.display(
      t("clients.detail.tabs.platformMappings.delete.confirmation", {
        platform: platformMapping.platform.name,
        code: platformMapping.code,
      }),
    );
  };

  const PlatformMappingsOptions = (
    <div className="flex flex-row gap-x-1 items-center">
      <SearchInput
        value={platformMappingSearch}
        onChangeHandler={filterPlatformMappingsByCode}
        placeholder="clients.detail.tabs.platformMappings.searchInputPlaceholder"
      />
      <CreatePlatformClientMappingDialog
        id={id}
        onSubmitHandler={onPlatformMappingCreation}
      />
    </div>
  );

  useEffect(() => {
    findClient();
    findClientMarkets();
    findClientCampaigns();
    findClientCategories();
    findClientProducts();
    findClientPlatformMappings();
    trackEvent({
      page: "client-detail",
      event: "page-load",
      data: { id: id },
    });
  }, [id]);

  return (
    <>
      {clientDeleteDialog.renderSafeDialog(deleteClient)}
      {marketDeleteDialog.renderSafeDialog(deleteMarket)}
      {campaignDeleteDialog.renderSafeDialog(deleteCampaign)}
      {categoryDeleteDialog.renderSafeDialog(deleteCategory)}
      {productDeleteDialog.renderSafeDialog(deleteProduct)}
      {platformMappingDeleteDialog.renderSafeDialog(deletePlatformMapping)}
      <div className="px-4 pb-4 sm:px-6">
        <BreadcrumbPanel items={breadcrumbItems} />
      </div>
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-row justify-between gap-x-4 px-4">
          <ClientDetail
            client={client}
            onDeletion={onDeleteClientClick}
            onEdit={editClient}
          />
          <div></div>
        </div>
        <ClientDetailTabMenu
          market={{
            data: markets,
            options: MarketsOptions,
            edit: onMarketEdition,
            delete: onMarketDeletionClick,
          }}
          campaign={{
            data: campaigns,
            options: CampaignsOptions,
            edit: onCampaignEdition,
            delete: onCampaignDeletionClick,
          }}
          category={{
            data: categories,
            options: CategoriesOptions,
            edit: onCategoryEdition,
            delete: onCategoryDeletionClick,
          }}
          product={{
            data: products,
            options: ProductsOptions,
            edit: onProductEdition,
            delete: onProductDeletionClick,
          }}
          platformMapping={{
            data: platformMappings,
            options: PlatformMappingsOptions,
            edit: onPlatformMappingEdition,
            delete: onPlatformMappingDeletionClick,
          }}
        />
      </div>
    </>
  );
};

export default ClientDetailContainer;
